import { useEffect, useRef, useState } from "react";
import { AlertTriangle, BadgePercent, Camera, Check, PackageSearch, Users, CheckCircle2, Clock, Heart, Hourglass, ImagePlus, Loader2, MessagesSquare, MinusCircle, PackageX, Pencil, Radio, Repeat, Send, ShieldAlert, ThumbsUp, Trash2, X, Zap } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

const TONE = {
  red: "border-red-300 bg-red-50 text-red-700 hover:bg-red-100",
  amber: "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100",
  sky: "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100",
  green: "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
  slate: "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100",
};
export const PRESETS = {
  prep: [
    { k: "oos", label: "Bu üründen kalmadı", icon: PackageX, tone: "red", product: true },
    { k: "low", label: "Stokta az var", icon: AlertTriangle, tone: "amber", product: true },
    { k: "alt", label: "Alternatif ürün var — onaylıyor musunuz?", icon: Repeat, tone: "sky", product: true },
    { k: "damaged", label: "Ürün hasarlı / kusurlu", icon: ShieldAlert, tone: "red", product: true },
    { k: "ready", label: "Hazırlık tamamlandı, teslime hazır", icon: CheckCircle2, tone: "green" },
    { k: "wait", label: "Müşteri geldi mi? Teslim bekleniyor", icon: Clock, tone: "slate" },
  ],
  cart: [
    { k: "remove", label: "Tamam, sepetten çıkarıyorum", icon: MinusCircle, tone: "slate" },
    { k: "altok", label: "Alternatifi koyabilirsiniz", icon: ThumbsUp, tone: "green" },
    { k: "ask", label: "Bekleyin, müşteriye soruyorum", icon: Hourglass, tone: "amber" },
    { k: "rush", label: "Acele — müşteri bekliyor", icon: Zap, tone: "red" },
    { k: "thanks", label: "Teşekkürler", icon: Heart, tone: "sky" },
  ],
  // Hazırlık listesi yokken sepet içi mesajlaşma
  team: [
    { k: "price", label: "Fiyat / iskonto onayı gerekiyor", icon: BadgePercent, tone: "amber" },
    { k: "stock", label: "Bu ürün stokta var mı?", icon: PackageSearch, tone: "sky", product: true },
    { k: "rush", label: "Acele — müşteri bekliyor", icon: Zap, tone: "red" },
    { k: "ask", label: "Bekleyin, müşteriye soruyorum", icon: Hourglass, tone: "amber" },
    { k: "okay", label: "Tamam, anlaşıldı", icon: ThumbsUp, tone: "green" },
    { k: "thanks", label: "Teşekkürler", icon: Heart, tone: "sky" },
  ],
};
const ALL = [...PRESETS.prep, ...PRESETS.cart, ...PRESETS.team];
const hm = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const ACT = "inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10.5px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground";

// Mesaj altı işlemleri: kendi mesajı → Düzenle / Sil, yönetici → Sil (dokunmatikte hep görünür, farede üzerine gelince)
function MsgActions({ m, canEdit, canDelete, onEdit, onDelete }) {
  const [ask, setAsk] = useState(false);
  if (!canEdit && !canDelete) return null;
  if (ask) {
    return (
      <div className="flex items-center gap-1 mt-0.5 px-1" data-testid={`chat-msg-delete-confirm-${m.id}`}>
        <span className="text-[10.5px] font-semibold text-red-600">Mesaj silinsin mi?</span>
        <button type="button" className={cn(ACT, "text-red-600 hover:text-red-700")} onClick={() => { setAsk(false); onDelete(); }} data-testid={`chat-msg-delete-yes-${m.id}`}>Sil</button>
        <button type="button" className={ACT} onClick={() => setAsk(false)} data-testid={`chat-msg-delete-no-${m.id}`}>Vazgeç</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-0.5 mt-0.5 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-within:opacity-100">
      {canEdit && <button type="button" className={ACT} onClick={onEdit} data-testid={`chat-msg-edit-${m.id}`}><Pencil className="h-3 w-3" />Düzenle</button>}
      {canDelete && <button type="button" className={cn(ACT, "hover:text-red-600")} onClick={() => setAsk(true)} data-testid={`chat-msg-delete-${m.id}`}><Trash2 className="h-3 w-3" />Sil</button>}
    </div>
  );
}

function EditBox({ m, onSave, onCancel }) {
  const [val, setVal] = useState(m.text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); const ok = await onSave(val.trim()); setBusy(false); if (ok) onCancel(); };
  return (
    <div className="w-full space-y-1" data-testid={`chat-msg-editbox-${m.id}`}>
      <textarea autoFocus rows={2} value={val} maxLength={1000} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } else if (e.key === "Escape") onCancel(); }}
        className="w-full min-w-[220px] resize-y rounded-xl border border-primary/50 bg-background text-foreground px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid={`chat-msg-edit-input-${m.id}`} />
      <div className="flex justify-end gap-1">
        <button type="button" className={ACT} onClick={onCancel} data-testid={`chat-msg-edit-cancel-${m.id}`}><X className="h-3 w-3" />İptal</button>
        <button type="button" disabled={busy || (!val.trim() && !m.photo_url)} onClick={save} className="inline-flex h-6 items-center gap-1 rounded-md bg-primary px-2 text-[10.5px] font-bold text-white disabled:opacity-40" data-testid={`chat-msg-edit-save-${m.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}Kaydet
        </button>
      </div>
    </div>
  );
}

function Bubble({ m, mine, isAdmin, cl }) {
  const [editing, setEditing] = useState(false);
  const p = ALL.find((x) => x.k === m.preset);
  const Icon = p?.icon;
  if (m.deleted) {
    return (
      <div className={cn("flex flex-col max-w-[85%]", mine ? "ml-auto items-end" : "items-start")} data-testid={`chat-msg-${m.id}`}>
        <div className="text-[10px] text-muted-foreground mb-0.5 px-1"><b className="text-foreground/70">{m.from_name}</b> · {hm(m.created_at)}</div>
        <div className="rounded-2xl border border-dashed border-border bg-muted/40 px-3 py-1.5 text-[12px] italic text-muted-foreground inline-flex items-center gap-1.5" data-testid={`chat-msg-deleted-${m.id}`}>
          <Trash2 className="h-3 w-3" />Bu mesaj silindi{m.deleted_by && m.deleted_by !== m.from_name ? ` · ${m.deleted_by}` : ""}
        </div>
      </div>
    );
  }
  return (
    <div className={cn("group flex flex-col max-w-[85%] animate-in fade-in slide-in-from-bottom-1 duration-200", mine ? "ml-auto items-end" : "items-start")} data-testid={`chat-msg-${m.id}`}>
      <div className="text-[10px] text-muted-foreground mb-0.5 px-1">
        <b className="text-foreground/80">{m.from_name}</b> · {m.side === "prep" ? "Depo" : "Sepet"} · {hm(m.created_at)}
        {m.edited_at && <span className="italic" title={`Düzenlendi ${hm(m.edited_at)}`} data-testid={`chat-msg-edited-${m.id}`}> · düzenlendi</span>}
      </div>
      {editing ? <EditBox m={m} onSave={(t) => cl.edit(m.id, t)} onCancel={() => setEditing(false)} /> : (
      <div className={cn("rounded-2xl px-3 py-2 text-[13px] leading-snug shadow-sm space-y-1.5", mine ? "bg-[#0B192C] text-white rounded-br-md" : "bg-card border border-border rounded-bl-md")}>
        {p && <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold", mine ? "border-white/25 bg-white/10 text-white" : TONE[p.tone])}><Icon className="h-3 w-3" />{mine ? "Hızlı mesaj" : "Hızlı mesaj"}</span>}
        {m.product_name && <div className={cn("text-[11px] font-bold", mine ? "text-[#FF8A4C]" : "text-primary")} data-testid={`chat-msg-product-${m.id}`}>Ürün: {m.product_name}</div>}
        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
        {m.photo_url && (
          <a href={fileUrl(m.photo_url)} target="_blank" rel="noreferrer" className="block">
            <img src={fileUrl(m.photo_url)} alt="fotoğraf" className="rounded-xl max-h-56 max-w-full object-contain bg-black/5" data-testid={`chat-msg-photo-${m.id}`} />
          </a>
        )}
      </div>)}
      {!editing && <MsgActions m={m} canEdit={mine} canDelete={mine || isAdmin} onEdit={() => setEditing(true)} onDelete={() => cl.remove(m.id)} />}
    </div>
  );
}

function ProductPick({ products, value, onChange, pending }) {
  if (!products.length) return null;
  return (
    <div className={cn("rounded-xl border px-2 py-1.5 transition-colors", pending ? "border-red-400 bg-red-50/70 ring-2 ring-red-300/50" : "border-border bg-muted/40")} data-testid="chat-product-pick">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{pending ? `"${pending.label}" — hangi ürün?` : "Ürün (opsiyonel)"}</div>
      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
        {products.map((p, i) => (
          <button key={p.key || i} type="button" onClick={() => onChange(value?.key === p.key ? null : p)}
            className={cn("shrink-0 max-w-[180px] truncate rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all active:scale-95", value?.key === p.key ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-primary/50")}
            data-testid={`chat-product-${i}`}>{p.name}</button>
        ))}
      </div>
    </div>
  );
}

// Sepet ↔ Depo mesajlaşma paneli (hazır seçenekler · ürün etiketi · yazı · fotoğraf)
export function CartChatSheet({ open, onOpenChange, side, cl, title, subtitle, products = [], userId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [text, setText] = useState("");
  const [product, setProduct] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const msgs = cl.messages;
  const teamMode = side === "cart" && !cl.prep;

  useEffect(() => { if (open) { cl.markRead(); setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 60); } }, [open, msgs.length]); // eslint-disable-line
  useEffect(() => { if (!open) { setPending(null); } }, [open]);

  const send = async (payload) => {
    setBusy(true);
    const ok = await cl.send({ side, product_id: product?.key || null, product_name: product?.name || "", ...payload });
    setBusy(false);
    if (ok) { setText(""); setProduct(null); setPending(null); }
  };
  const applyPreset = (p, prod = product) => {
    if (p.product && !prod) { setPending(p); return; }
    send({ preset: p.k, text: [p.label, text.trim()].filter(Boolean).join(" — "), product_id: prod?.key || null, product_name: prod?.name || "" });
  };
  const pickProduct = (p) => { setProduct(p); if (pending && p) applyPreset(pending, p); };
  const sendPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await cl.send({ side, photo_url: up.data.url, text: text.trim(), product_id: product?.key || null, product_name: product?.name || "" });
      setText(""); setProduct(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0 z-[60] [&>button:first-child]:text-white [&>button:first-child]:z-10" data-testid="cart-chat-sheet">
        <div className="shrink-0 bg-[#0B192C] text-white px-4 pt-4 pb-3">
          <SheetTitle className="text-white font-head text-lg flex items-center gap-2" data-testid="cart-chat-title"><MessagesSquare className="h-5 w-5 text-[#FF8A4C]" />{teamMode ? "Sepet Mesajları" : "Sepet ↔ Depo Mesajları"}</SheetTitle>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-white/70">
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/90 text-white px-2 py-0.5 font-bold"><Radio className="h-3 w-3 animate-pulse" />CANLI</span>
            <span className="truncate">{title}{subtitle ? ` · ${subtitle}` : ""}</span>
          </div>
          {teamMode && <div className="mt-1.5 flex items-center gap-1.5 text-[10.5px] text-white/60" data-testid="cart-chat-team-note"><Users className="h-3 w-3 shrink-0" />Mesajlar sepetteki kişilere ve yöneticilere gider — hazırlık listesi gerekmez</div>}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3 bg-muted/30" data-testid="cart-chat-list">
          {!msgs.length && <div className="h-full grid place-items-center text-center text-sm text-muted-foreground px-6"><div><MessagesSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />Henüz mesaj yok. Aşağıdaki hazır seçeneklerle tek dokunuşla mesaj gönderebilirsiniz.</div></div>}
          {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.from_id === userId} isAdmin={isAdmin} cl={cl} />)}
          <div ref={endRef} />
        </div>
        <div className="shrink-0 border-t border-border bg-card px-3 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2">
          <div className="grid grid-cols-2 gap-1.5" data-testid="chat-presets">
            {(teamMode ? PRESETS.team : PRESETS[side]).map((p) => (
              <button key={p.k} type="button" disabled={busy} onClick={() => applyPreset(p)}
                className={cn("flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-left text-[11.5px] font-semibold leading-tight transition-all active:scale-[0.98] disabled:opacity-50", TONE[p.tone], pending?.k === p.k && "ring-2 ring-offset-1 ring-red-400")}
                data-testid={`chat-preset-${p.k}`}><p.icon className="h-4 w-4 shrink-0" /><span>{p.label}</span></button>
            ))}
          </div>
          <ProductPick products={products} value={product} onChange={pickProduct} pending={pending} />
          <div className="flex items-end gap-1.5">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="h-10 w-10 shrink-0 grid place-items-center rounded-xl border border-border bg-muted/50 text-muted-foreground hover:text-foreground active:scale-95" title="Fotoğraf gönder" data-testid="chat-photo-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => sendPhoto(e.target.files?.[0])} data-testid="chat-photo-input" />
            <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (text.trim()) send({ text: text.trim() }); } }}
              placeholder="Mesaj yazın…" className="flex-1 min-h-10 max-h-28 resize-none rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="chat-text" />
            <button type="button" onClick={() => send({ text: text.trim() })} disabled={busy || !text.trim()} className="h-10 w-10 shrink-0 grid place-items-center rounded-xl bg-primary text-white shadow-md shadow-primary/30 disabled:opacity-40 active:scale-95" data-testid="chat-send">
              <Send className="h-4 w-4" />
            </button>
          </div>
          {pending && <button type="button" onClick={() => setPending(null)} className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><X className="h-3 w-3" />Seçimi iptal et</button>}
          <p className="text-[10px] text-muted-foreground flex items-center gap-1"><ImagePlus className="h-3 w-3" />Fotoğraf kameradan çekilebilir · mesajlar karşı tarafa anında düşer</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
